import React, { useState, useEffect, useMemo } from "react";
import "./Dashboard.css";

const Dashboard = ({ items, inventory, config, onTabChange, apiCall, activeHospitalId }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiCall("/dashboard")
      .then((data) => { if (!cancelled) setDashboardData(data); })
      .catch(() => { if (!cancelled) setDashboardData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiCall, activeHospitalId, items, inventory]);

  const stats = useMemo(() => {
    if (dashboardData) {
      return {
        total: dashboardData.totalItems ?? 0,
        inStock: dashboardData.itemsWithStock ?? 0,
        lowStock: dashboardData.lowStockCount ?? 0,
        needReorder: dashboardData.reorderNeededCount ?? 0,
        expiringSoon: dashboardData.expiringCount ?? 0,
        expired: dashboardData.expiredCount ?? 0,
      };
    }

    const allItems = items || [];
    const allInventory = inventory || [];
    const today = new Date();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;

    let inStock = 0, lowStock = 0, needReorder = 0, expiringSoon = 0, expired = 0;

    allItems.forEach((item) => {
      const itemInventries = allInventory.filter((inv) => inv.itemId === item.id);
      const stock = itemInventries.reduce((sum, inv) => sum + (inv.currentStock || 0), 0);
      const minLevel = item.minStockLevel ?? item.reorderPoint ?? 0;
      const reorderPoint = item.reorderPoint ?? item.minStockLevel ?? 0;

      if (stock > 0) inStock++;
      if (stock <= minLevel) lowStock++;
      if (stock <= reorderPoint) needReorder++;
    });

    allInventory.forEach((inv) => {
      if (!inv.expirationDate) return;
      // Parse at local midnight — a bare "YYYY-MM-DD" is treated as UTC by
      // Date(), which shifts the day for negative-offset timezones.
      const expiry = new Date(inv.expirationDate + "T00:00:00");
      if (expiry < today) expired++;
      else if (expiry - today <= thirtyDays) expiringSoon++;
    });

    return {
      total: allItems.length,
      inStock,
      lowStock,
      needReorder,
      expiringSoon,
      expired,
    };
  }, [dashboardData, items, inventory]);

  const alerts = useMemo(() => {
    // Server alerts (from /api/dashboard) have a different shape than the
    // render below expects — normalize them: 'low-stock' → {count, minStockLevel},
    // 'expired'/'expiring' → {id (lot id), date}. No stock on expiry alerts.
    if (dashboardData?.alerts) {
      return dashboardData.alerts.slice(0, 10).map((a) => {
        if (a.type === "low-stock") {
          const count = a.count ?? 0;
          return {
            type: count <= 0 ? "outOfStock" : "lowStock",
            itemId: a.id,
            name: a.name,
            stock: count,
            action: count <= 0
              ? "Out of stock: order immediately"
              : `Low stock: ${count} left (min ${a.minStockLevel ?? 0})`,
            tab: "reorder",
          };
        }
        return {
          type: a.type, // "expired" | "expiring"
          itemId: a.id,
          name: a.name,
          stock: null,
          date: a.date,
          action: a.type === "expired"
            ? "Expired: remove or dispose"
            : "Expiring soon: use first or reorder",
          tab: "inventory",
        };
      });
    }

    const result = [];
    const today = new Date();
    // Expiry warning window comes from the hospital's config (default 30).
    const warningDays = Number(config?.expirationWarningDays) > 0 ? Number(config.expirationWarningDays) : 30;
    const warningMs = warningDays * 24 * 60 * 60 * 1000;
    const allItems = items || [];
    const allInventory = inventory || [];
    // itemId → item lookup — Map avoids an O(n²) find() inside the lot loop.
    const itemsById = new Map(allItems.map((i) => [i.id, i]));

    allItems.forEach((item) => {
      const itemInventries = allInventory.filter((inv) => inv.itemId === item.id);
      const stock = itemInventries.reduce((sum, inv) => sum + (inv.currentStock || 0), 0);
      const minLevel = item.minStockLevel ?? item.reorderPoint ?? 0;

      if (stock <= minLevel && stock > 0) {
        result.push({
          type: "lowStock",
          itemId: item.id,
          name: item.name,
          stock,
          action: "Reorder recommended: stock is low",
          tab: "reorder",
        });
      } else if (stock <= 0) {
        result.push({
          type: "outOfStock",
          itemId: item.id,
          name: item.name,
          stock,
          action: "Out of stock: order immediately",
          tab: "reorder",
        });
      }
    });

    allInventory.forEach((inv) => {
      if (!inv.expirationDate) return;
      // Parse at local midnight — a bare "YYYY-MM-DD" is treated as UTC by
      // Date(), which shifts the day for negative-offset timezones.
      const expiry = new Date(inv.expirationDate + "T00:00:00");
      const item = itemsById.get(inv.itemId);
      if (expiry < today) {
        result.push({
          type: "expired",
          itemId: inv.itemId,
          name: item?.name || inv.lotNumber || "Unknown",
          stock: inv.currentStock,
          action: "Expired: remove or dispose",
          tab: "inventory",
        });
      } else if (expiry - today <= warningMs) {
        result.push({
          type: "expiring",
          itemId: inv.itemId,
          name: item?.name || inv.lotNumber || "Unknown",
          stock: inv.currentStock,
          action: "Expiring soon: use first or reorder",
          tab: "inventory",
        });
      }
    });

    return result.slice(0, 10);
  }, [dashboardData, items, inventory, config]);

  const statCards = [
    { label: "Total Items", value: stats.total, className: "stat-value--info", tab: "items" },
    { label: "In Stock", value: stats.inStock, className: "stat-value--ok", tab: "inventory" },
    { label: "Low Stock", value: stats.lowStock, className: "stat-value--reorder", tab: "reorder" },
    { label: "Need Reorder", value: stats.needReorder, className: "stat-value--low", tab: "reorder" },
    { label: "Expiring Soon", value: stats.expiringSoon, className: "stat-value--reorder", tab: "inventory" },
    { label: "Expired", value: stats.expired, className: "stat-value--low", tab: "inventory" },
  ];

  const alertTypeClass = (type) => {
    if (type === "outOfStock" || type === "expired") return "dashboard-alert-card--low";
    if (type === "lowStock" || type === "expiring") return "dashboard-alert-card--reorder";
    return "";
  };

  // Alert dates are bare "YYYY-MM-DD" strings — parse at local midnight so
  // the displayed day isn't shifted in negative-offset timezones.
  const formatAlertDate = (date) => {
    const d = new Date(String(date).includes("T") ? date : date + "T00:00:00");
    return isNaN(d.getTime()) ? String(date) : d.toLocaleDateString();
  };

  if (loading) {
    return <div className="loading">Loading dashboard…</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-section">
        <h2 className="section-title">Overview</h2>
        <div className="dashboard-stats">
          {statCards.map((card) => (
            <div
              key={card.label}
              className="stat-card"
              onClick={() => onTabChange && onTabChange(card.tab)}
              style={onTabChange ? { cursor: "pointer" } : undefined}
              role={onTabChange ? "button" : undefined}
              tabIndex={onTabChange ? 0 : undefined}
              onKeyDown={onTabChange ? (e) => e.key === "Enter" && onTabChange(card.tab) : undefined}
              title={onTabChange ? `Click to view ${card.label} details` : undefined}
            >
              <h3>{card.label}</h3>
              <span className={`stat-value ${card.className}`}>{card.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard-section">
          <h2 className="section-title">Alerts</h2>
          {alerts.length > 0 ? (
            <div className="dashboard-alerts">
              {alerts.map((alert, idx) => (
                <div
                  key={`${alert.type}-${alert.itemId}-${idx}`}
                  className={`dashboard-alert-card ${alertTypeClass(alert.type)}`}
                  onClick={() => onTabChange(alert.tab)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && onTabChange(alert.tab)}
                >
                  <div className="dashboard-alert-card__name">{alert.name}</div>
                  <div className="dashboard-alert-card__stock">
                    {alert.stock != null
                      ? `Stock: ${alert.stock}`
                      : alert.date
                        ? `${alert.type === "expired" ? "Expired" : "Expires"}: ${formatAlertDate(alert.date)}`
                        : ""}
                  </div>
                  <div className="dashboard-alert-card__action">{alert.action}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="dashboard-empty">
              No alerts. All stock levels are adequate and no items are expiring soon.
            </div>
          )}
        </div>

      <div className="dashboard-section">
        <h2 className="section-title">Quick Actions</h2>
        <div className="dashboard-quick-actions">
          <button className="btn btn-primary" onClick={() => onTabChange("inventory")}>
            Update Stock
          </button>
          <button className="btn btn-secondary" onClick={() => onTabChange("reorder")}>
            View Reorder List
          </button>
          <button className="btn btn-outline" onClick={() => onTabChange("items")}>
            Manage Items
          </button>
        </div>
      </div>

      <div className="info-box info-box--blue">
        <strong>How to use</strong>
        <p style={{ margin: "0.5rem 0 0", color: "var(--text-secondary)", lineHeight: 1.6 }}>
          The stat cards show counts at a glance: total items, items in stock, low stock, needing reorder, expiring soon, and expired. The alert cards below list each item that needs action, with its stock and a suggested action. Click a quick-action button to jump straight to the relevant tab.
        </p>
        <div style={{ marginTop: "0.5rem", background: "var(--surface-card)", border: "1px solid var(--status-info-border)", borderRadius: "0.375rem", padding: "0.5rem 0.75rem", color: "var(--status-info-text)" }}>
          <strong>Example:</strong> The "Expired" card shows 3 items. Below, an alert card reads "CBC Reagent — Expired 5d ago — Action: Archive". Click <strong>Update Stock</strong> to go to Current Inventory and archive it.
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
